import {
	DateString,
	Highlight,
	HighlightedText,
	Icon,
	IconValue,
	PersonAvatar,
	PipeSeparator
} from "client/Components";
import { getComputedDisplayName, getContactPhotoUrl } from "client/Features/AddressBook/common";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { EmailGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useMemo } from "react";
import { ItemLayout } from "./ItemLayout";
import styles from "./EmailItemView.module.less";
import { EmailImportance } from "client/Api/Email/common";
import { EmailActions } from "client/Api/Email/email";
import { TimeZone } from "client/DateAndTime/timezones";
import { useEmailNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useEmailNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { generateTrackingId } from "client/Misc/utils";
import { Key } from "client/Misc/commonTypes";
import { useKeyboardListNavigationItem } from "client/Hooks";

export type EmailItemViewProps = {
	item: EmailGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	timezone: TimeZone;
};

export const EmailItemView = (props: EmailItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		timezone
	} = props;

	const emailId: string = item.item
		.emailId
		.id;

	const executeHandler: () => void = useEmailNavigationHandler({
		emailId: emailId,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("email-item", `|${emailId}`)}
		>
			<EmailItem
				item={item}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
EmailItemView.displayName = "EmailItemView";

type EmailItemProps = {
	item: EmailGlobalSearchResultItem;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const EmailItem = (props: EmailItemProps): ReactElement => {
	const {
		item: {
			item: {
				subject,
				ownerName,
				from,
				receivedDateTime,
				preview,
				lastAction,
				isMeetingMessage,
				hasAttachments,
				isFlagged,
				importance
			},
			hitHighlights,
			parentFolderName
		},
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const {
		initials
	} = getComputedDisplayName(
		{
			displayName: ownerName
		}
		);

	const statusIcons = useMemo(
		(): readonly IconValue[] => {
			const hasReply: boolean = lastAction === EmailActions.Replied;
			const hasForward: boolean = lastAction === EmailActions.Forwarded;
			const isHighImportance: boolean = importance === EmailImportance.High;
			const isLowImportance: boolean = importance === EmailImportance.Low;

			const intermediateResult: readonly (IconValue | false)[] = [
				hasReply && "reply",
				hasForward && "share",
				isMeetingMessage && "calendar",
				hasAttachments && "paperclip",
				isFlagged && { kind: "font", name: "flag-filled", color: "primary" },
				isLowImportance && "arrow-down",
				isHighImportance && "triangle-exclamation"
			];

			return intermediateResult.filter((x): x is IconValue => Boolean(x));
		},
		[
			hasAttachments,
			importance,
			isFlagged,
			isMeetingMessage,
			lastAction
		]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<PersonAvatar
					initials={initials}
					imageSrc={getContactPhotoUrl(from)}
					size="small"
				/>
			</ItemLayout.IconContainer>

			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="title"
					title={subject || localize("_NoSubject")}
					rightAddition={statusIcons.length > 0
						? (
							<ul className={styles.statusIcons}>
								{statusIcons.map((x, i): ReactElement => (
									<li key={i}>
										<Icon value={x} />
									</li>
								))}
							</ul>
						)
						: undefined
					}
				>
					<HighlightedText
						text={subject || localize("_NoSubject")}
						highlight={highlights.get("item.subject")}
					/>
				</ItemLayout.Title>

				<ItemLayout.Info>
					{ownerName && (
						<ItemLayout.SubTitle trackingId="owner">
							<HighlightedText
								text={ownerName}
								highlight={highlights.get("item.ownerName")}
							/>
						</ItemLayout.SubTitle>
					)}

					{receivedDateTime && (
						<>
							{ownerName && (
								<PipeSeparator />
							)}

							<ItemLayout.SubTitle>
								<DateString
									format="AbsoluteTimestamp"
									value={receivedDateTime}
									timezone={timezone}
								/>
							</ItemLayout.SubTitle>
						</>
					)}

					{(ownerName || receivedDateTime) && (
						<PipeSeparator />
					)}

					<ItemLayout.InfoIconWrapper>
						<Icon value="folder-open" size="small" />
					</ItemLayout.InfoIconWrapper>

					<ItemLayout.SubTitle>
						{parentFolderName}
					</ItemLayout.SubTitle>
				</ItemLayout.Info>

				<ItemLayout.Content>
					<HighlightedText
						text={preview || localize("_NoPreviewAvailable")}
						highlight={highlights.get("item.preview")}
					/>
				</ItemLayout.Content>
			</ItemLayout.InfoWrapper>
		</ItemLayout>
	);
};
EmailItem.displayName = "EmailItem";
